import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { CalendarDays, Clock, ArrowLeft, Loader2, UserCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";

function readingTime(content) {
  const words = (content || "").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

function setMetaDescription(content) {
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", "description");
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const { data } = await api.get(`/blog/posts/${slug}`);
      setPost(data);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.seo_title || post.title} — Eazy-Travels`;
    setMetaDescription(post.seo_description || post.excerpt || post.title);
  }, [post]);

  if (loading) {
    return (
      <>
        <Header />
        <main className="bg-[var(--bg)] min-h-[60vh] grid place-items-center pt-20">
          <Loader2 size={28} className="text-[var(--gold)] animate-spin" />
        </main>
        <Footer />
      </>
    );
  }

  if (notFound || !post) {
    return (
      <>
        <Header />
        <main className="bg-[var(--bg)] min-h-[60vh] grid place-items-center pt-20 text-center px-4">
          <div data-testid="blog-post-not-found">
            <h1 className="font-display text-3xl font-bold text-white mb-3">Post not found</h1>
            <p className="text-white/60 mb-6">This article may have been unpublished or moved.</p>
            <Link to="/blog" className="btn-gold text-sm">
              <ArrowLeft size={14} /> Back to Blog
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="bg-[var(--bg)]">
        <article className="pt-28 pb-20" data-testid={`blog-post-${post.slug}`}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/blog" className="text-sm text-white/50 hover:text-[var(--gold)] inline-flex items-center gap-1.5 mb-8">
              <ArrowLeft size={14} /> Back to Blog
            </Link>

            {post.category && <p className="overline mb-3">{post.category}</p>}
            <h1 className="font-display text-4xl sm:text-5xl font-black text-white leading-[1.1] mb-5">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-5 text-sm text-white/45 mb-10">
              {post.author_name && (
                <span className="flex items-center gap-1.5"><UserCircle size={15} /> {post.author_name}</span>
              )}
              <span className="flex items-center gap-1.5">
                <CalendarDays size={15} /> {new Date(post.published_at || post.created_at).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock size={15} /> {readingTime(post.content)} min read
              </span>
            </div>

            {post.featured_image && (
              <div className="rounded-2xl overflow-hidden gold-glow mb-12">
                <img src={post.featured_image} alt={post.title} className="w-full max-h-[480px] object-cover" />
              </div>
            )}

            <div className="prose prose-invert prose-headings:font-display prose-a:text-[var(--gold)] max-w-none">
              <ReactMarkdown>{post.content}</ReactMarkdown>
            </div>

            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-white/5">
                {post.tags.map((tag) => (
                  <span key={tag} className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/60">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
